import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useProfiles } from "@/lib/profiles";
import LetterDocument from "@/components/LetterDocument";
import { printLetter } from "@/lib/printLetter";
import { getLetterhead } from "@/lib/letterheads";
import { getTemplate, templateDefaults, buildBody, buildSubject } from "@/lib/letterTemplates";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowLeft, Save, Printer, Bold, Italic, Underline, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  List, ListOrdered, Table as TableIcon, Undo2, Redo2, Eraser, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";

const fieldCls = "bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-2 focus-visible:ring-0 focus-visible:border-[#D4AF37] text-white font-mont placeholder:text-neutral-600";
const labelCls = "font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500";
const selCls = "w-full bg-[#121212] border-0 border-b border-white/20 text-white font-mont text-sm py-2 focus:outline-none focus:border-[#D4AF37]";
const taCls = "w-full bg-transparent border-0 border-b border-white/20 px-0 py-2 text-white font-mont text-sm placeholder:text-neutral-600 focus:outline-none focus:border-[#D4AF37] resize-y";
const panelCls = "border border-white/10 bg-[#0F0F0F] p-5 space-y-4";
const PAGE_PX = 794; // 210mm at 96dpi

const todayLocal = () => {
  const d = new Date();
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
};

function TBtn({ title, onClick, children }) {
  return (
    <button
      type="button"
      title={title}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
      className="w-8 h-8 flex items-center justify-center text-neutral-300 hover:text-white hover:bg-white/10"
    >
      {children}
    </button>
  );
}

export default function LetterEditor() {
  const { id } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const { profiles, activeProfileId } = useProfiles();

  const bodyRef = useRef(null);
  const lastRange = useRef(null);
  const scrollRef = useRef(null);
  const loadedId = useRef("");

  const [form, setForm] = useState(null);
  const [letterId, setLetterId] = useState(id || "");
  const [bodyEdited, setBodyEdited] = useState(false);
  const [subjectEdited, setSubjectEdited] = useState(false);
  const [resetKey, setResetKey] = useState(0);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [scale, setScale] = useState(1);

  const profile = profiles.find((p) => p.id === form?.profile_id) || profiles[0];
  const company = getLetterhead(profile).companyName;
  const tpl = getTemplate(form?.template);

  // ---- load existing letter or start a new one from a template ----
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (id) {
        if (loadedId.current === id) return; // just created and navigated here
        try {
          const { data } = await api.get(`/letters/${id}`);
          if (cancelled) return;
          loadedId.current = id;
          setForm(data); setLetterId(data.id);
          setBodyEdited(true); setSubjectEdited(true);
          setResetKey((k) => k + 1); setDirty(false);
        } catch (err) {
          toast.error(err?.response?.data?.detail || "Could not open this letter");
          navigate("/letters", { replace: true });
        }
      } else {
        if (!profiles.length) return;
        const prof = profiles.find((p) => p.id === activeProfileId) || profiles[0];
        const t = getTemplate(sp.get("template") || "blank");
        const fields = templateDefaults(t);
        const co = getLetterhead(prof).companyName;
        if (cancelled) return;
        setLetterId("");
        setForm({
          profile_id: prof.id, template: t.key, title: "", heading: t.heading, ref_no: "", date: todayLocal(),
          address_mode: t.toWhomItMayConcern ? "twimc" : "to",
          recipient_name: "", recipient_designation: "", recipient_address: "",
          subject: buildSubject(t, fields), body_html: buildBody(t, fields, "", co),
          signatory_name: "", signatory_designation: "Director", show_signature_image: true, show_acceptance: !!t.acceptance, font_size: 10.5, fields,
        });
        setBodyEdited(false); setSubjectEdited(false);
        setResetKey((k) => k + 1); setDirty(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line
  }, [id, profiles.length]);

  // ---- fit the A4 page to the available width ----
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return undefined;
    const calc = () => setScale(Math.max(0.35, Math.min(1, (el.clientWidth - 24) / PAGE_PX)));
    calc();
    const ro = new ResizeObserver(calc);
    ro.observe(el);
    return () => ro.disconnect();
  }, [!!form]); // eslint-disable-line

  // ---- remember the text selection inside the editor (toolbar selects steal focus) ----
  useEffect(() => {
    const h = () => {
      const sel = window.getSelection();
      if (sel && sel.rangeCount && bodyRef.current && bodyRef.current.contains(sel.anchorNode)) {
        lastRange.current = sel.getRangeAt(0).cloneRange();
      }
    };
    document.addEventListener("selectionchange", h);
    return () => document.removeEventListener("selectionchange", h);
  }, []);

  useEffect(() => {
    if (!dirty) return undefined;
    const h = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", h);
    return () => window.removeEventListener("beforeunload", h);
  }, [dirty]);

  // ---- form updates ----
  const update = (patch, { regen = false } = {}) => {
    const next = { ...form, ...patch };
    if (regen) {
      const t = getTemplate(next.template);
      if (!bodyEdited) {
        next.body_html = buildBody(t, next.fields, next.recipient_name, company);
        setResetKey((k) => k + 1);
      }
      if (!subjectEdited) next.subject = buildSubject(t, next.fields);
    }
    setForm(next);
    setDirty(true);
  };

  const applyDetails = () => {
    if (!window.confirm("Replace the letter text with fresh text built from the details? Your manual edits to the text will be lost.")) return;
    setForm({
      ...form,
      body_html: buildBody(tpl, form.fields, form.recipient_name, company),
      subject: buildSubject(tpl, form.fields),
    });
    setBodyEdited(false); setSubjectEdited(false);
    setResetKey((k) => k + 1); setDirty(true);
  };

  const syncBody = useCallback(() => {
    if (!bodyRef.current) return;
    const html = bodyRef.current.innerHTML;
    setForm((f) => (f ? { ...f, body_html: html } : f));
    setBodyEdited(true);
    setDirty(true);
  }, []);

  // ---- rich text toolbar ----
  const restoreSel = () => {
    const el = bodyRef.current;
    if (!el) return false;
    el.focus();
    const sel = window.getSelection();
    if (lastRange.current) { sel.removeAllRanges(); sel.addRange(lastRange.current); }
    return true;
  };
  const exec = (cmd, val = null) => {
    if (!restoreSel()) return;
    document.execCommand("styleWithCSS", false, true);
    document.execCommand(cmd, false, val);
    syncBody();
  };
  const setFontSize = (pt) => {
    if (!pt || !restoreSel()) return;
    document.execCommand("styleWithCSS", false, false);
    document.execCommand("fontSize", false, "7");
    bodyRef.current.querySelectorAll('font[size="7"]').forEach((f) => {
      const s = document.createElement("span");
      s.style.fontSize = `${pt}pt`;
      while (f.firstChild) s.appendChild(f.firstChild);
      f.replaceWith(s);
    });
    syncBody();
  };
  const insertTable = () => {
    let h = "<table><tbody>";
    for (let r = 0; r < 3; r += 1) h += `<tr>${"<td>&nbsp;</td>".repeat(3)}</tr>`;
    h += "</tbody></table><p><br></p>";
    exec("insertHTML", h);
  };

  // ---- save / print ----
  const currentLetter = () => ({
    ...form,
    body_html: bodyRef.current ? bodyRef.current.innerHTML : form.body_html,
    title: form.title?.trim() || `${tpl.label}${form.recipient_name ? ` — ${form.recipient_name}` : ""}`,
  });

  const save = async () => {
    if (saving) return;
    if (!form.profile_id) { toast.error("No business selected"); return; }
    setSaving(true);
    try {
      const payload = currentLetter();
      let data;
      if (letterId) {
        ({ data } = await api.put(`/letters/${letterId}`, payload));
      } else {
        ({ data } = await api.post("/letters", payload));
      }
      setForm((f) => ({ ...f, ...data, body_html: payload.body_html }));
      setDirty(false);
      toast.success(letterId ? "Letter saved" : `Letter saved (${data.ref_no})`);
      if (!letterId) {
        setLetterId(data.id);
        loadedId.current = data.id;
        navigate(`/letters/${data.id}`, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to save letter");
    } finally {
      setSaving(false);
    }
  };

  const doPrint = () => printLetter(currentLetter(), profile);

  const goBack = () => {
    if (dirty && !window.confirm("You have unsaved changes. Leave without saving?")) return;
    navigate("/letters");
  };

  if (!form) {
    return <div className="p-10 text-neutral-500 font-mont text-xs uppercase tracking-[0.3em]">Loading…</div>;
  }

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <button onClick={goBack} className="flex items-center gap-2 font-mont text-[10px] uppercase tracking-[0.25em] text-neutral-500 hover:text-white">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Letters
        </button>
        <div className="flex items-center gap-3">
          {dirty && <span className="font-mont text-[10px] uppercase tracking-widest text-[#D4AF37]">Unsaved changes</span>}
          <Button data-testid="save-letter-btn" onClick={save} disabled={saving} className="rounded-none bg-white text-black hover:bg-neutral-200 disabled:opacity-50">
            <Save className="w-4 h-4 mr-2" strokeWidth={1.5} /> {saving ? "Saving…" : "Save"}
          </Button>
          <Button data-testid="print-letter-btn" onClick={doPrint} variant="outline" className="rounded-none border-white/20 bg-transparent text-white hover:bg-white/10">
            <Printer className="w-4 h-4 mr-2" strokeWidth={1.5} /> Print / PDF
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[340px_minmax(0,1fr)] gap-6 items-start">
        {/* ---------------- left: details ---------------- */}
        <aside className="space-y-5 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:overflow-y-auto xl:pr-1">
          <div className={panelCls}>
            <div className="font-serif text-lg text-white">{tpl.label}</div>
            <div>
              <Label className={labelCls}>Name in your list (optional)</Label>
              <Input value={form.title} onChange={(e) => update({ title: e.target.value })} placeholder="Auto: letter type + person" className={fieldCls} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className={labelCls}>Reference no.</Label>
                <Input value={form.ref_no} onChange={(e) => update({ ref_no: e.target.value })} placeholder="Auto on save" className={fieldCls} />
              </div>
              <div>
                <Label className={labelCls}>Date</Label>
                <Input type="date" value={form.date} onChange={(e) => update({ date: e.target.value })} className={fieldCls + " [color-scheme:dark]"} />
              </div>
            </div>
            <div>
              <Label className={labelCls}>Heading on the letter</Label>
              <Input value={form.heading} onChange={(e) => update({ heading: e.target.value })} placeholder="e.g. APPOINTMENT LETTER" className={fieldCls} />
            </div>
          </div>

          <div className={panelCls}>
            <div className={labelCls}>Addressed to</div>
            <select value={form.address_mode} onChange={(e) => update({ address_mode: e.target.value })} className={selCls}>
              <option value="to">A person (To, name, address)</option>
              <option value="twimc">To Whom It May Concern</option>
              <option value="none">No addressee</option>
            </select>
            <div>
              <Label className={labelCls}>{form.address_mode === "twimc" ? "Person the letter is about" : "Name"}</Label>
              <Input data-testid="recipient-name" value={form.recipient_name} onChange={(e) => update({ recipient_name: e.target.value }, { regen: true })} className={fieldCls} />
            </div>
            {form.address_mode === "to" && (
              <>
                <div>
                  <Label className={labelCls}>Designation / company</Label>
                  <Input value={form.recipient_designation} onChange={(e) => update({ recipient_designation: e.target.value })} className={fieldCls} />
                </div>
                <div>
                  <Label className={labelCls}>Address</Label>
                  <textarea rows={3} value={form.recipient_address} onChange={(e) => update({ recipient_address: e.target.value })} className={taCls} />
                </div>
              </>
            )}
            <div>
              <Label className={labelCls}>Subject</Label>
              <Input value={form.subject} onChange={(e) => { setSubjectEdited(true); update({ subject: e.target.value }); }} className={fieldCls} />
            </div>
          </div>

          {tpl.fields.length > 0 && (
            <div className={panelCls}>
              <div className={labelCls}>Details for this letter</div>
              {tpl.fields.map((f) => (
                <div key={f.key}>
                  <Label className={labelCls}>{f.label}</Label>
                  {f.type === "textarea" ? (
                    <textarea
                      rows={4}
                      value={form.fields[f.key] || ""}
                      placeholder={f.placeholder}
                      onChange={(e) => update({ fields: { ...form.fields, [f.key]: e.target.value } }, { regen: true })}
                      className={taCls}
                    />
                  ) : (
                    <Input
                      type={f.type || "text"}
                      step={f.type === "number" ? "any" : undefined}
                      value={form.fields[f.key] || ""}
                      placeholder={f.placeholder}
                      onChange={(e) => update({ fields: { ...form.fields, [f.key]: e.target.value } }, { regen: true })}
                      className={fieldCls + (f.type === "date" ? " [color-scheme:dark]" : "")}
                    />
                  )}
                </div>
              ))}
              {bodyEdited ? (
                <button onClick={applyDetails} className="flex items-center gap-2 font-mont text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] hover:text-white">
                  <RefreshCw className="w-3.5 h-3.5" strokeWidth={1.5} /> Rebuild letter text from details
                </button>
              ) : (
                <p className="font-mont text-[10px] text-neutral-600 leading-relaxed">The letter text updates as you fill these in. Once you edit the text directly on the page, use "Rebuild" to apply new details.</p>
              )}
            </div>
          )}

          <div className={panelCls}>
            <div className={labelCls}>Signature</div>
            <div>
              <Label className={labelCls}>Signatory name</Label>
              <Input value={form.signatory_name} onChange={(e) => update({ signatory_name: e.target.value })} className={fieldCls} />
            </div>
            <div>
              <Label className={labelCls}>Signatory designation</Label>
              <Input value={form.signatory_designation} onChange={(e) => update({ signatory_designation: e.target.value })} className={fieldCls} />
            </div>
            {profile?.signature_url && (
              <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
                <input type="checkbox" checked={!!form.show_signature_image} onChange={(e) => update({ show_signature_image: e.target.checked })} />
                Show saved signature image
              </label>
            )}
            <label className="flex items-center gap-2 text-sm text-neutral-300 cursor-pointer">
              <input type="checkbox" checked={!!form.show_acceptance} onChange={(e) => update({ show_acceptance: e.target.checked })} />
              Add employee acceptance / acknowledgement block
            </label>
            <div>
              <Label className={labelCls}>Text size</Label>
              <select value={form.font_size} onChange={(e) => update({ font_size: Number(e.target.value) })} className={selCls}>
                {[9.5, 10, 10.5, 11, 11.5, 12].map((s) => (<option key={s} value={s}>{s} pt</option>))}
              </select>
            </div>
          </div>
        </aside>

        {/* ---------------- right: page ---------------- */}
        <section className="min-w-0">
          <div className="sticky top-0 z-20 bg-[#0A0A0A] border border-white/10 flex flex-wrap items-center gap-1 p-1.5 mb-3">
            <TBtn title="Undo" onClick={() => exec("undo")}><Undo2 className="w-4 h-4" strokeWidth={1.5} /></TBtn>
            <TBtn title="Redo" onClick={() => exec("redo")}><Redo2 className="w-4 h-4" strokeWidth={1.5} /></TBtn>
            <span className="w-px h-5 bg-white/10 mx-1" />
            <select
              defaultValue=""
              onChange={(e) => { if (e.target.value) exec("formatBlock", e.target.value); e.target.value = ""; }}
              className="bg-[#121212] border border-white/10 text-neutral-300 font-mont text-xs h-8 px-2"
              title="Text style"
            >
              <option value="">Style</option>
              <option value="<p>">Paragraph</option>
              <option value="<h2>">Heading</option>
              <option value="<h3>">Sub-heading</option>
            </select>
            <select
              defaultValue=""
              onChange={(e) => { setFontSize(e.target.value); e.target.value = ""; }}
              className="bg-[#121212] border border-white/10 text-neutral-300 font-mont text-xs h-8 px-2"
              title="Size of selected text"
            >
              <option value="">Size</option>
              {[9, 10, 11, 12, 14, 16, 18, 22].map((s) => (<option key={s} value={s}>{s} pt</option>))}
            </select>
            <span className="w-px h-5 bg-white/10 mx-1" />
            <TBtn title="Bold" onClick={() => exec("bold")}><Bold className="w-4 h-4" strokeWidth={1.75} /></TBtn>
            <TBtn title="Italic" onClick={() => exec("italic")}><Italic className="w-4 h-4" strokeWidth={1.75} /></TBtn>
            <TBtn title="Underline" onClick={() => exec("underline")}><Underline className="w-4 h-4" strokeWidth={1.75} /></TBtn>
            <span className="w-px h-5 bg-white/10 mx-1" />
            <TBtn title="Align left" onClick={() => exec("justifyLeft")}><AlignLeft className="w-4 h-4" strokeWidth={1.5} /></TBtn>
            <TBtn title="Centre" onClick={() => exec("justifyCenter")}><AlignCenter className="w-4 h-4" strokeWidth={1.5} /></TBtn>
            <TBtn title="Align right" onClick={() => exec("justifyRight")}><AlignRight className="w-4 h-4" strokeWidth={1.5} /></TBtn>
            <TBtn title="Justify" onClick={() => exec("justifyFull")}><AlignJustify className="w-4 h-4" strokeWidth={1.5} /></TBtn>
            <span className="w-px h-5 bg-white/10 mx-1" />
            <TBtn title="Bullet list" onClick={() => exec("insertUnorderedList")}><List className="w-4 h-4" strokeWidth={1.5} /></TBtn>
            <TBtn title="Numbered list" onClick={() => exec("insertOrderedList")}><ListOrdered className="w-4 h-4" strokeWidth={1.5} /></TBtn>
            <TBtn title="Insert table (3 × 3)" onClick={insertTable}><TableIcon className="w-4 h-4" strokeWidth={1.5} /></TBtn>
            <TBtn title="Clear formatting" onClick={() => exec("removeFormat")}><Eraser className="w-4 h-4" strokeWidth={1.5} /></TBtn>
          </div>

          <div ref={scrollRef} className="bg-[#2b2b2b] p-3 sm:p-4 overflow-x-hidden">
            <div style={{ zoom: scale, width: "210mm", margin: "0 auto", boxShadow: "0 4px 30px rgba(0,0,0,0.5)" }}>
              <LetterDocument
                letter={form}
                profile={profile}
                mode="screen"
                editable
                bodyRef={bodyRef}
                onBodyInput={syncBody}
                bodyResetKey={resetKey}
              />
            </div>
          </div>
          <p className="font-mont text-[10px] text-neutral-600 mt-3 leading-relaxed">
            Click on the letter text to edit it directly. Long letters continue on extra pages when printed. In the print window choose "Save as PDF" to download a PDF.
          </p>
        </section>
      </div>
    </div>
  );
}
